import { TokenManager } from '@/services/auth.js'

/**
 * JWT Token自动刷新管理器
 */
export class TokenRefreshManager {
    constructor() {
        this.refreshTimer = null
        this.REFRESH_THRESHOLD = 5 * 60 * 1000 // 5分钟前刷新
    }

    /**
     * 启动自动刷新
     */
    startAutoRefresh() {
        this.stopAutoRefresh()
        this.scheduleRefresh()
    }

    /**
     * 停止自动刷新
     */
    stopAutoRefresh() {
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer)
            this.refreshTimer = null
        }
    }

    /**
     * 计划下次刷新
     */
    scheduleRefresh() {
        const token = TokenManager.getAccessToken()
        if (!token) return

        try {
            const payload = JSON.parse(atob(token.split('.')[1]))
            const expirationTime = payload.exp * 1000
            const currentTime = Date.now()
            const timeUntilRefresh = expirationTime - currentTime - this.REFRESH_THRESHOLD

            if (timeUntilRefresh > 0) {
                this.refreshTimer = setTimeout(() => {
                    this.performRefresh()
                }, timeUntilRefresh)
            } else {
                // Token即将过期，立即刷新
                this.performRefresh()
            }
        } catch (error) {
            console.error('解析token失败:', error)
        }
    }

    /**
     * 执行token刷新
     */
    async performRefresh() {
        try {
            const refreshToken = TokenManager.getRefreshToken()
            if (!refreshToken) {
                console.warn('没有refresh token，无法刷新')
                return
            }

            const { refreshAccessToken } = await import('@/services/auth.js')
            const newTokens = await refreshAccessToken(refreshToken)

            TokenManager.setTokens(newTokens)
            console.log('Token刷新成功')

            // 计划下次刷新
            this.scheduleRefresh()
        } catch (error) {
            console.error('Token刷新失败:', error)
            // 刷新失败，清除所有token
            TokenManager.clearTokens()
            // 可以触发重新登录流程
            window.location.href = '/'
        }
    }
}

// 创建全局实例
export const tokenRefreshManager = new TokenRefreshManager()

/**
 * 权限检查工具
 */
export class PermissionChecker {
    static rolePermissions = {
        'USER': ['bet', 'view_market', 'manage_profile'],
        'OPERATOR': ['bet', 'view_market', 'manage_profile', 'manage_market', 'manage_user', 'view_admin_panel'],
        'ADMIN': ['*'] // 拥有所有权限
    }

    /**
     * 检查用户是否有指定权限
     */
    static hasPermission(userRole, permission) {
        if (!userRole) return false

        const permissions = this.rolePermissions[userRole] || []

        // 管理员拥有所有权限
        if (permissions.includes('*')) return true

        return permissions.includes(permission)
    }

    /**
     * 检查用户是否有任一指定权限
     */
    static hasAnyPermission(userRole, permissions) {
        return permissions.some(permission => this.hasPermission(userRole, permission))
    }

    /**
     * 检查用户是否有所有指定权限
     */
    static hasAllPermissions(userRole, permissions) {
        return permissions.every(permission => this.hasPermission(userRole, permission))
    }

    /**
     * 获取用户的所有权限
     */
    static getUserPermissions(userRole) {
        if (!userRole) return []

        const permissions = this.rolePermissions[userRole] || []

        // 如果是管理员，返回所有可能的权限
        if (permissions.includes('*')) {
            return Object.values(this.rolePermissions).flat().filter(p => p !== '*')
        }

        return permissions
    }
}

/**
 * 路由权限检查
 */
export const checkRoutePermission = (
    routePermission,
    userRole,
    isAuthenticated = false
) => {
    // 如果需要认证但用户未登录
    if (routePermission.requireAuth && !isAuthenticated) {
        return false
    }

    // 如果不需要认证
    if (!routePermission.requireAuth) {
        return true
    }

    // 检查角色权限
    if (routePermission.roles && routePermission.roles.length > 0) {
        if (!userRole || !routePermission.roles.includes(userRole)) {
            return false
        }
    }

    // 检查具体权限
    if (routePermission.permissions && routePermission.permissions.length > 0) {
        if (!userRole) return false

        return PermissionChecker.hasAllPermissions(userRole, routePermission.permissions)
    }

    return true
}

/**
 * 认证状态监听器
 */
export class AuthStateListener {
    static listeners = []

    /**
     * 添加认证状态变化监听器
     */
    static addListener(callback) {
        this.listeners.push(callback)
    }

    /**
     * 移除监听器
     */
    static removeListener(callback) {
        const index = this.listeners.indexOf(callback)
        if (index > -1) {
            this.listeners.splice(index, 1)
        }
    }

    /**
     * 通知所有监听器
     */
    static notifyListeners(isAuthenticated) {
        this.listeners.forEach(callback => {
            try {
                callback(isAuthenticated)
            } catch (error) {
                console.error('认证状态监听器执行失败:', error)
            }
        })
    }
}
